import type { InfoPageContent } from './infoTypes';

export const howItWorksContent: InfoPageContent = {
  title: 'How It Works',
  tagline: 'From a thousand public markets to a short list of deeply validated candidates — all inside your browser.',
  lastUpdated: 'August 7, 2026',
  sections: [
    {
      heading: '1 · A continuous whole-market radar',
      paragraphs: [
        'A dedicated Web Worker keeps public Binance spot and perpetual markets under constant observation over WebSocket streams. Bybit and OKX provide independent snapshots and can sustain the lightweight radar when fresh Binance market data is unavailable.',
        'The radar maintains rolling per-market baselines for short-window price, volume, spread, local high/low recovery, acceleration, and data freshness — so “unusual” is always measured against that market’s own recent behaviour, not a global average.',
      ],
      bullets: [
        'Hidden recovery: a coin can still be negative over 24 hours while recovering from a deeper recent low.',
        'Hidden weakness: a coin can remain positive over 24 hours while deteriorating from a recent high.',
      ],
    },
    {
      heading: '2 · Cheap scanning, expensive validation',
      paragraphs: [
        'Scanning the broad market is deliberately cheap. Only leading observations are promoted for deeper analysis, and unusually strong early observations may enter provisional deep validation after a short observation window.',
        'This adaptive funnel keeps the terminal responsive while spending the expensive work — full candle history, structure, and support/resistance analysis — only where it is justified.',
      ],
    },
    {
      heading: '3 · Deep validation requires real history',
      paragraphs: [
        'Deep validation pulls the selected horizon plus the relevant 1H, 4H, 1D, and 1W candle hierarchy from the exchange that verified the candle source. Candle-close state is exchange-confirmed; nothing is assumed from the device clock.',
        'Historical breakthrough depth is discovered from the chart itself, never fixed to a predefined candle count. Support and resistance zones carry history, touch count, strength, age, and distance.',
      ],
    },
    {
      heading: '4 · Promotion is gated, not forced',
      paragraphs: [
        'A candidate only reaches the Predictor when it passes baseline, parent-timeframe, timing, liquidity, and data-quality gates. Near-close candle gating prevents a fresh candidate from being promoted when its candle is almost finished.',
        'Everything else stays visible in Eagle Watch with its validation status and blockers shown. When nothing passes, the Predictor abstains and states the concrete reason.',
      ],
    },
    {
      heading: '5 · Evidence stays separated from the move',
      paragraphs: [
        'The coin page separates 24-hour venue participation (who traded) from the cause of the current move. Funding behaviour, open interest, supply, DEX liquidity, and market drivers are each shown with their source and confidence — and marked unavailable rather than guessed when not confidently resolved.',
      ],
    },
    {
      heading: '6 · The operating boundary',
      paragraphs: [
        'Radar monitoring works while the application is open in your browser. Browsers may suspend inactive or closed tabs, so Pilum does not pretend to provide background monitoring after the browser is closed.',
      ],
      note: 'Browser monitoring stops when the tab is closed. For continuous alerts you would need to keep the app open — this is a browser-first tool by design.',
    },
  ],
};
